import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import KundaliChart from "../components/KundaliChart";
import BirthDetailsModal from "../components/BirthDetailsModal";
import OrderHistory from "../components/OrderHistory";
import VipKingBadge from "../components/VipKingBadge";
import WelcomeBonusModal from "../components/WelcomeBonusModal";
import api, { resolveAssetUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { Wallet, ChatCircleDots, Plus, Sparkle, PencilSimple, MoonStars, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLANET_NAMES = ["Sun", "Moon", "Mars", "Mercury", "Jupiter", "Venus", "Saturn", "Rahu", "Ketu"];

export default function CustomerDashboardPage() {
  const { user, loading, checkAuth } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [kundali, setKundali] = useState(null);
  const [birthDetails, setBirthDetails] = useState(null);
  const [showBirthModal, setShowBirthModal] = useState(false);

  const fetchKundali = useCallback(async () => {
    try {
      const { data } = await api.get("/customer/kundali");
      setKundali(data.kundali);
      setBirthDetails(data.birth_details);
    } catch (err) {
      // 404 = no birth details submitted yet — empty state
      setKundali(null);
      setBirthDetails(null);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    api.get("/chats").then(({ data }) => setChats(data));
    fetchKundali();
  }, [user, loading, navigate, fetchKundali]);

  // Smooth-scroll to the section requested by the mobile-menu hash link
  // (e.g. /dashboard#chats, /dashboard#wallet). Runs after initial data load
  // so the section actually exists in the DOM.
  useEffect(() => {
    if (!user) return;
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const tryScroll = (attempt = 0) => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempt < 5) {
        setTimeout(() => tryScroll(attempt + 1), 200);
      }
    };
    tryScroll();
  }, [user]);

  const handleBirthSubmit = async ({ date, time, place, timezone }) => {
    try {
      await api.post("/customer/birth-details", { date, time, place, timezone });
      await checkAuth();
      await fetchKundali();
      setShowBirthModal(false);
      toast.success("Your kundali is ready");
    } catch (err) {
      console.error(err);
      toast.error("Could not save your birth details.");
    }
  };

  if (!user) return null;

  return (
    <PageShell>
      <WelcomeBonusModal />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 pb-24" data-testid="customer-dashboard">
        <p className="label-eyebrow">Welcome back</p>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light">{user.name?.split(" ")[0]}</h1>
          {user.is_vip && <VipKingBadge label="VIP KING" className="!text-xs !px-3 !py-1" />}
        </div>
        {user.is_vip && (
          <p className="text-muted text-sm mt-2" data-testid="vip-credit-remaining">
            👑 Royal seeker · ₹{Number(user.vip_credit_remaining || 0).toFixed(0)} VIP credit remaining
          </p>
        )}

        <div className="grid md:grid-cols-3 gap-4 mt-10" id="wallet">
          <div className="card-surface rounded-md p-7 md:col-span-1" data-testid="wallet-card">
            <Wallet size={26} weight="duotone" className="text-gold" />
            <p className="label-eyebrow mt-4">Wallet balance</p>
            <div className="font-serif-display text-5xl mt-2">{formatPrice(user.wallet_balance || 0)}</div>
            <button
              onClick={() => navigate("/wallet/topup")}
              className="btn-primary rounded-full px-5 py-2.5 text-sm mt-5 flex items-center gap-2"
              data-testid="topup-btn"
            >
              <Plus size={14} weight="bold" /> Add credits
            </button>
          </div>
          <Link to="/astrologers" className="card-surface rounded-md p-7 md:col-span-2 flex flex-col justify-between" data-testid="browse-cta-card">
            <div>
              <ChatCircleDots size={26} weight="duotone" className="text-gold" />
              <p className="label-eyebrow mt-4">Start a new reading</p>
              <h3 className="font-serif-display text-3xl mt-2 font-light max-w-md">Find the perfect astrologer for what's on your mind tonight.</h3>
            </div>
            <span className="text-gold mt-6 text-sm">Browse astrologers →</span>
          </Link>
        </div>

        {/* ORDER HISTORY */}
        <div className="mt-6">
          <OrderHistory />
        </div>

        {/* KUNDALI SECTION */}
        <div className="mt-16" data-testid="kundali-section">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="label-eyebrow flex items-center gap-2 text-gold">
                <Sparkle size={14} weight="fill" /> Your Vedic profile
              </p>
              <h2 className="font-serif-display text-4xl mt-3 font-light">Your kundali</h2>
            </div>
            {kundali && (
              <button onClick={() => setShowBirthModal(true)} className="btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-2" data-testid="edit-birth-btn">
                <PencilSimple size={13} /> Edit birth details
              </button>
            )}
          </div>

          {!kundali ? (
            <div className="card-surface rounded-md p-10 mt-8 text-center" data-testid="kundali-empty-state">
              <MoonStars size={36} weight="duotone" className="text-gold mx-auto" />
              <h3 className="font-serif-display text-2xl mt-4 font-light">Unlock your kundali</h3>
              <p className="text-muted text-sm mt-3 max-w-md mx-auto font-light">
                Share your birth date, time and place. We'll compute your accurate Vedic chart —
                Lagna, Moon nakshatra, Vimshottari dashas, all 9 grahas — and your AI Astrology Experts will
                read from it immediately.
              </p>
              <button onClick={() => setShowBirthModal(true)} className="btn-primary rounded-full px-6 py-3 text-sm font-medium mt-6" data-testid="generate-kundali-btn">
                Generate my kundali
              </button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-12 gap-6 mt-8">
              {/* Diamond chart */}
              <div className="lg:col-span-5 card-surface rounded-md p-6 flex flex-col items-center" data-testid="kundali-chart-card">
                <KundaliChart kundali={kundali} size={340} />
                <p className="label-eyebrow text-center mt-5">
                  Lahiri ayanamsa · {birthDetails?.place}
                </p>
                <p className="text-muted text-xs mt-1.5 font-light text-center">
                  Born {birthDetails?.date} · {birthDetails?.time} · {birthDetails?.timezone}
                </p>
              </div>

              {/* Summary */}
              <div className="lg:col-span-7 space-y-4">
                {/* Lagna + Moon */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="card-surface rounded-md p-5">
                    <p className="label-eyebrow">Lagna (Ascendant)</p>
                    <div className="font-serif-display text-3xl mt-2 text-gold">{kundali.lagna.rashi}</div>
                    <p className="text-muted text-xs mt-1">
                      {kundali.lagna.degrees_in_rashi}° · Lord: {kundali.lagna.rashi_lord}
                    </p>
                  </div>
                  <div className="card-surface rounded-md p-5">
                    <p className="label-eyebrow">Moon</p>
                    <div className="font-serif-display text-3xl mt-2 text-gold">{kundali.moon.rashi}</div>
                    <p className="text-muted text-xs mt-1">
                      {kundali.moon.nakshatra} · pada {kundali.moon.pada}
                    </p>
                  </div>
                </div>

                {/* Current dashas */}
                <div className="card-surface rounded-md p-5">
                  <p className="label-eyebrow flex items-center gap-2"><Star size={12} weight="fill" className="text-gold" /> Current Dasha</p>
                  <div className="flex flex-wrap items-baseline gap-x-4 mt-3">
                    <div>
                      <span className="font-serif-display text-2xl text-gold">{kundali.current_mahadasha.lord}</span>
                      <span className="text-muted text-xs ml-2">Mahadasha</span>
                    </div>
                    <span className="text-muted text-xs">{kundali.current_mahadasha.from} → {kundali.current_mahadasha.to}</span>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-4 mt-2 pt-3 border-t border-soft">
                    <div>
                      <span className="font-serif-display text-xl">{kundali.current_antardasha.lord}</span>
                      <span className="text-muted text-xs ml-2">Antardasha</span>
                    </div>
                    <span className="text-muted text-xs">{kundali.current_antardasha.from} → {kundali.current_antardasha.to}</span>
                  </div>
                  {kundali.upcoming_mahadashas?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-soft">
                      <p className="label-eyebrow mb-2">Upcoming Mahadashas</p>
                      <div className="space-y-1.5">
                        {kundali.upcoming_mahadashas.map((u) => (
                          <div key={u.lord} className="flex justify-between text-xs">
                            <span className="text-gold">{u.lord}</span>
                            <span className="text-muted">{u.from} → {u.to} <span className="opacity-60">({u.years} yr)</span></span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Planets table */}
                <div className="card-surface rounded-md p-5" data-testid="planets-table">
                  <p className="label-eyebrow">Planetary positions (sidereal)</p>
                  <div className="grid grid-cols-3 gap-x-4 gap-y-2 mt-3 text-xs">
                    {PLANET_NAMES.map((name) => {
                      const p = kundali.planets[name];
                      if (!p) return null;
                      return (
                        <div key={name} className="flex justify-between items-baseline border-b border-soft/50 pb-1">
                          <span className="text-gold font-medium">{name}{p.retrograde ? "ᴿ" : ""}</span>
                          <span className="text-muted">{p.rashi.slice(0, 3)} {p.degrees_in_rashi.toFixed(1)}°</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-16" id="chats">
          <p className="label-eyebrow">Conversation history</p>
          <h2 className="font-serif-display text-4xl mt-3 font-light">Your chats</h2>
          <div className="mt-8 space-y-3">
            {chats.length === 0 ? (
              <div className="text-muted py-10">No conversations yet. <Link to="/astrologers" className="text-gold">Start one</Link>.</div>
            ) : chats.map((c) => (
              <Link to={`/chat/${c.chat_id}`} key={c.chat_id} className="card-surface rounded-md p-5 flex items-center gap-4" data-testid={`chat-row-${c.chat_id}`}>
                <img src={resolveAssetUrl(c.astrologer_avatar)} alt="" className="w-14 h-14 rounded-full object-cover" />
                <div className="flex-1">
                  <div className="font-serif-display text-xl">{c.astrologer_name}</div>
                  <div className="text-muted text-sm mt-1">{new Date(c.started_at).toLocaleString()}</div>
                </div>
                <span className={`label-eyebrow px-3 py-1 rounded-full border ${c.status === "active" ? "border-green-500/30 text-green-400" : "border-soft text-muted"}`}>{c.status}</span>
              </Link>
            ))}
          </div>
        </div>

        {showBirthModal && (
          <BirthDetailsModal
            astrologerName="your guide"
            onSubmit={handleBirthSubmit}
            onSkip={() => setShowBirthModal(false)}
          />
        )}
      </div>
    </PageShell>
  );
}
